import { useEffect, useState } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'
import { currentShift, db, dayTotals, tallyShift, topProductsToday, type DayTotals } from './db'
import { onSync } from './sync'
import { useNow } from './hooks'

/**
 * Số liệu màn hình chính. Hai bộ giao diện vẽ khác nhau nhưng đọc chung một chỗ —
 * hai màn hình cùng tên mà ra hai con số khác nhau thì chủ quán không tin cái nào nữa.
 */
export function useHomeData() {
  const now = useNow(60_000)

  // Chủ ở nhà nhìn số của cả quán — phải biết số đó mới tới lúc nào, không thì không
  // phân biệt được "quán chưa bán gì" với "máy chưa kéo được dữ liệu về".
  const [lastPullAt, setLastPullAt] = useState<string | null>(null)
  useEffect(() => onSync((s) => setLastPullAt(s.lastPullAt)), [])

  const totals = useLiveQuery(() => dayTotals(), [], null)
  const shift = useLiveQuery(() => currentShift(), [])
  const shiftTally = useLiveQuery(
    async () => (shift ? await tallyShift(shift) : null),
    [shift, now],
    null,
  )
  const top = useLiveQuery(() => topProductsToday(4), [], [])
  const busy = useLiveQuery(() => db.orders.where('status').equals('Open').count(), [], 0)
  const spendCount = useLiveQuery(
    () => db.expenses.where('spentAt').aboveOrEqual(startOfToday()).count(),
    [],
    0,
  )

  return { now, lastPullAt, totals, shift, shiftTally, top, busy, spendCount }
}

/**
 * Doanh thu hôm qua, để so với hôm nay.
 *
 * Trả về `null` khi máy này chưa có đơn nào của hôm qua — máy mới cài, hoặc quán hôm qua
 * nghỉ. Khi đó giao diện phải nói "chưa có số hôm qua" chứ không được vẽ mũi tên 0%:
 * một con số không có thật nằm cạnh doanh thu là thứ chủ quán sẽ tin.
 */
export function useYesterday(): DayTotals | null {
  return (
    useLiveQuery(async () => {
      const yesterday = new Date()
      yesterday.setDate(yesterday.getDate() - 1)

      const t = await dayTotals(yesterday)
      return t.orderCount > 0 ? t : null
    }, [], null) ?? null
  )
}

/**
 * Doanh thu dồn theo giờ trong ngày, để vẽ đường nhỏ sau lưng con số.
 *
 * Dồn chứ không phải từng giờ rời: đường dồn luôn đi lên, đọc một cái là thấy quán đang
 * chạy tới đâu. Đường từng giờ thì nhấp nhô, nhìn như báo động lúc vắng khách.
 */
export function useTodayCurve(): number[] {
  return (
    useLiveQuery(async () => {
      const from = startOfToday()
      const paid = await db.orders
        .where('paidAt')
        .aboveOrEqual(from)
        .filter((o) => o.status === 'Paid')
        .toArray()

      const perHour = new Array<number>(24).fill(0)
      for (const o of paid) {
        if (!o.paidAt) continue
        const h = new Date(o.paidAt).getHours()
        perHour[h] = (perHour[h] ?? 0) + o.total
      }

      let running = 0
      return perHour.map((v) => (running += v))
    }, [], []) ?? []
  )
}

function startOfToday(): string {
  const d = new Date()
  d.setHours(0, 0, 0, 0)
  return d.toISOString()
}
